import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Box } from '@mui/material';

import { OfferFragment } from '@occc/graphql';
import { useChainId } from '@occc-app/Chain/hooks';
import { FormContainerProps } from '@occc-app/offerHelpers/types';
import { useGetConfigureFormProps } from '@occc-app/offerHelpers/hooks/useGetConfigureFormProps';
import { LoadingOverlay, Typography } from '@occc/ui-design-system';

import { StoredValueConfigureForm } from './stored-value-configure-form.component';
import { getStoredValueConfigureFormOperations } from './stored-value-configure-form-operations';
import { transformStoredValueOfferToInitialValues } from './stored-value-configure-form-initial-values';

import { useGetStoredValueProgramById } from '../../data-access/queries';

export const StoredValueConfigureFormContainer = (props: FormContainerProps) => {
  const chainId = useChainId();
  const { storedValueProgramId = '' } = useParams<{ storedValueProgramId: string }>();

  const { data: program, loading: programLoading } = useGetStoredValueProgramById({
    id: storedValueProgramId,
    chainId,
  });

  const transformOfferToInitialValues = useMemo(
    () => (offer?: OfferFragment) =>
      transformStoredValueOfferToInitialValues(offer, chainId, program ?? undefined),
    [chainId, program],
  );

  const configureProps = useGetConfigureFormProps(
    props,
    transformOfferToInitialValues,
    getStoredValueConfigureFormOperations,
  );

  if (!programLoading && !program) {
    return (
      <Box position='relative' minHeight={240} display='flex' alignItems='center' px={2}>
        <Typography>Stored value program was not found.</Typography>
      </Box>
    );
  }

  if (programLoading || !props.offer || props.offerLoading) {
    return (
      <Box position='relative' minHeight={240}>
        <LoadingOverlay rounded />
      </Box>
    );
  }

  if (props.offer.id !== program?.discountingOffer.id) {
    return (
      <Box position='relative' minHeight={240} display='flex' alignItems='center' px={2}>
        <Typography>This offer does not match the selected stored value program.</Typography>
      </Box>
    );
  }

  return (
    <StoredValueConfigureForm
      {...configureProps}
      loading={configureProps.loading || programLoading}
    />
  );
};
